
import React, { useState, useEffect, useCallback } from 'react';
import { History, X, RotateCcw, Loader2, ChevronDown, ChevronUp, Trash2, Lock, Save } from 'lucide-react';
import { fetchVersions, saveVersion, deleteVersion, TranscriptVersion } from '../services/historyService';
import { auth } from '../services/supabase';

interface VersionHistoryPanelProps {
  transcriptId: string;
  currentTranscript: string;
  isPaid: boolean;
  onRestore: (transcript: string) => void;
}

/** Compute a simple line-level diff between two texts, returning segments marked as added/removed/same */
function computeDiff(oldText: string, newText: string): Array<{ type: 'same' | 'removed' | 'added'; line: string }> {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');

  const oldSet = new Set(oldLines);
  const newSet = new Set(newLines);

  const result: Array<{ type: 'same' | 'removed' | 'added'; line: string }> = [];

  // Use a simple LCS-based approach: mark lines as same/removed/added
  const maxLen = Math.max(oldLines.length, newLines.length);
  let oi = 0;
  let ni = 0;

  while (oi < oldLines.length || ni < newLines.length) {
    const oldLine = oldLines[oi];
    const newLine = newLines[ni];

    if (oi >= oldLines.length) {
      result.push({ type: 'added', line: newLine });
      ni++;
    } else if (ni >= newLines.length) {
      result.push({ type: 'removed', line: oldLine });
      oi++;
    } else if (oldLine === newLine) {
      result.push({ type: 'same', line: oldLine });
      oi++;
      ni++;
    } else {
      // Check if newLine appears later in old (line was removed) or oldLine appears later in new (line was added)
      const oldLineInNew = newLines.slice(ni).indexOf(oldLine);
      const newLineInOld = oldLines.slice(oi).indexOf(newLine);

      if (oldLineInNew === -1 && newLineInOld === -1) {
        result.push({ type: 'removed', line: oldLine });
        result.push({ type: 'added', line: newLine });
        oi++;
        ni++;
      } else if (newLineInOld !== -1 && (oldLineInNew === -1 || newLineInOld <= oldLineInNew)) {
        result.push({ type: 'added', line: newLine });
        ni++;
      } else {
        result.push({ type: 'removed', line: oldLine });
        oi++;
      }
    }
  }

  return result;
}

const DiffView: React.FC<{ version: TranscriptVersion; currentTranscript: string }> = ({ version, currentTranscript }) => {
  const diff = computeDiff(version.transcript, currentTranscript);

  return (
    <div className="font-mono text-xs leading-relaxed max-h-64 overflow-y-auto rounded-xl border border-base-200 dark:border-dark-300 bg-base-50 dark:bg-dark-100 p-3">
      {diff.map((segment, i) => (
        <div
          key={i}
          className={`px-2 py-0.5 rounded whitespace-pre-wrap ${
            segment.type === 'added'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
              : segment.type === 'removed'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 line-through opacity-60'
              : 'text-base-content/50 dark:text-dark-content/50'
          }`}
        >
          <span className="select-none mr-2 opacity-40">
            {segment.type === 'added' ? '+' : segment.type === 'removed' ? '-' : ' '}
          </span>
          {segment.line || ' '}
        </div>
      ))}
    </div>
  );
};

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  transcriptId,
  currentTranscript,
  isPaid,
  onRestore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [versions, setVersions] = useState<TranscriptVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const user = auth.currentUser;

  const loadVersions = useCallback(async () => {
    if (!transcriptId || !auth.currentUser) return;
    setIsLoading(true);
    try {
      const fetched = await fetchVersions(transcriptId);
      setVersions(fetched);
    } catch (e) {
      console.error('Failed to fetch versions', e);
    } finally {
      setIsLoading(false);
    }
  }, [transcriptId]);

  useEffect(() => {
    if (isOpen) loadVersions();
  }, [isOpen, loadVersions]);

  const handleSaveCurrentVersion = async () => {
    setIsSaving(true);
    try {
      await saveVersion(transcriptId, currentTranscript, `Saved ${new Date().toLocaleString()}`);
      await loadVersions();
    } catch (e) {
      console.error('Failed to save version', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async (version: TranscriptVersion) => {
    if (!version.id) return;
    setRestoringId(version.id);
    // Save current as a version before restoring (non-destructive)
    try {
      await saveVersion(transcriptId, currentTranscript, `Auto-saved before restore (${new Date().toLocaleString()})`);
      onRestore(version.transcript);
      await loadVersions();
    } catch (e) {
      console.error('Failed to restore version', e);
    } finally {
      setRestoringId(null);
    }
  };

  const handleDeleteVersion = async (versionId: string) => {
    try {
      await deleteVersion(transcriptId, versionId);
      setVersions(prev => prev.filter(v => v.id !== versionId));
      if (expandedId === versionId) setExpandedId(null);
    } catch (e) {
      console.error('Failed to delete version', e);
    }
  };

  if (!user) return null;

  if (!isPaid) {
    return (
      <button
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-500/20 bg-amber-500/5 text-sm font-semibold text-amber-600 dark:text-amber-400 cursor-default"
        title="Version history is a paid feature"
      >
        <Lock size={16} />
        <span>Version History</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(v => !v)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-base-300 dark:border-dark-300 bg-white dark:bg-dark-200 text-sm font-semibold text-base-content/70 hover:text-brand-primary hover:border-brand-primary/50 transition-all shadow-sm"
      >
        <History size={16} />
        <span>Version History</span>
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {isOpen && (
        <div className="fixed right-4 top-24 z-40 w-96 max-h-[80vh] bg-white dark:bg-dark-200 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 flex flex-col animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-base-200 dark:border-dark-300">
            <div className="flex items-center gap-2">
              <History size={16} className="text-brand-primary" />
              <span className="font-bold text-base-content dark:text-dark-content text-sm">Version History</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveCurrentVersion}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-brand-primary text-white font-semibold hover:bg-brand-dark disabled:opacity-40 transition-colors"
              >
                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save Current
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg hover:bg-base-200 dark:hover:bg-dark-300 text-base-content/40 hover:text-base-content transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Version list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 size={24} className="animate-spin text-brand-primary" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8 text-base-content/40 dark:text-dark-content/40">
                <History size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No versions saved yet.</p>
                <p className="text-xs mt-1">Click "Save Current" to create a snapshot.</p>
              </div>
            ) : (
              versions.map(version => (
                <div
                  key={version.id}
                  className="rounded-xl border border-base-200 dark:border-dark-300 bg-base-100 dark:bg-dark-100 overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-base-content dark:text-dark-content truncate">
                        {version.label || new Date(version.savedAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-base-content/40 dark:text-dark-content/40">
                        {version.transcript.length} characters
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button
                        onClick={() => setExpandedId(expandedId === version.id ? null : (version.id ?? null))}
                        className="p-1.5 rounded-lg hover:bg-base-200 dark:hover:bg-dark-300 text-base-content/40 hover:text-brand-primary transition-colors"
                        title="Show diff"
                      >
                        {expandedId === version.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      <button
                        onClick={() => version.id && handleRestore(version)}
                        disabled={restoringId === version.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-brand-primary/10 text-brand-primary font-semibold hover:bg-brand-primary/20 disabled:opacity-40 transition-colors"
                        title="Restore this version (current will be auto-saved)"
                      >
                        {restoringId === version.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <RotateCcw size={12} />
                        )}
                        Restore
                      </button>
                      <button
                        onClick={() => version.id && handleDeleteVersion(version.id)}
                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-base-content/30 hover:text-red-500 transition-colors"
                        title="Delete this version"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  {expandedId === version.id && (
                    <div className="px-4 pb-4 border-t border-base-200 dark:border-dark-300 pt-3">
                      <p className="text-xs font-semibold text-base-content/50 dark:text-dark-content/50 mb-2 uppercase tracking-wider">
                        Diff vs Current
                      </p>
                      <DiffView version={version} currentTranscript={currentTranscript} />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistoryPanel;
